import React, { useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";

import QuantityButtonInput from "../../Components/Common/QuantityButtonInput";
import { useUserStore } from "../../store/store";
import { ticketService } from "../../api/services/ticket.service";
import { useAuthContext } from "../../Components/AuthContext";
import { Button } from "../../Components/ui";
import { useTicketCartStore } from "../../store/ticketCartStore";

const CardSection = ({ amazingpass = false }) => {
  const navigate = useNavigate();

  const [tickets, setTickets] = React.useState([]);
  const { isUserLogin } = useAuthContext();
  const [user] = useUserStore((state) => [state.user]);
  const scrollRef = useRef(null);
  const cartItems = useTicketCartStore((state) => state.items);
  const setCartItemQuantity = useTicketCartStore((state) => state.setItemQuantity);
  const removeCartItem = useTicketCartStore((state) => state.removeItem);

  const cartQuantityById = useMemo(
    () =>
      cartItems.reduce((acc, item) => {
        acc[String(item.id)] = item.quantity;
        return acc;
      }, {}),
    [cartItems]
  );

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await ticketService.getTicketProducts();
        const data = response.data;

        const normalizedTickets = data.map((item) => {
          if (
            user &&
            user.membership !== "" &&
            item.membership_price !== 0 &&
            item.membership_price <= item.price
          ) {
            return { ...item, price: item.membership_price };
          }

          return item;
        });

        const adjustedCount = normalizedTickets.reduce((count, item) => {
          const syncResult = useTicketCartStore.getState().syncProduct(item);
          return count + (syncResult.adjusted ? 1 : 0);
        }, 0);

        if (adjustedCount > 0) {
          toast.info("재고 변동으로 장바구니 수량이 조정되었습니다.");
        }

        setTickets(
          normalizedTickets.filter((item) =>
            amazingpass ? item.state === "어메이징패스" : item.state !== "어메이징패스"
          )
        );
      } catch (error) {
        console.error("Fetching tickets failed", error);
      }
    };

    fetchData();
  }, [user]);

  const ticket_purchase_count = cartItems.reduce((total, item) => total + item.quantity, 0);
  const totalPrice = cartItems.reduce((total, item) => total + item.price * item.quantity, 0);

  return (
    <React.Fragment>
      {/* Page Header */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-160 z-40 bg-white/80 backdrop-blur-md px-4 py-3 border-b border-gray-200/60 shadow-sm">
        <h1 className="text-xl font-bold" style={{ fontFamily: "Gmarket-B" }}>티켓 구매</h1>
        <p className="text-sm text-gray-500 mt-1">자유이용권 및 입장권을 구매하세요.</p>
      </div>

      {/* Ticket List */}
      <div className="flex flex-col gap-3 px-4 pt-24 pb-48 bg-gray-100 min-h-screen">
        {tickets.map((item) => {
          const quantity = cartQuantityById[String(item.id)] || 0;
          const isSoldOut = item.state === "품절";
          return (
            <div
              key={item.id}
              className="rounded-xl p-4 mb-3 transition-colors duration-150"
              style={{
                backgroundColor: quantity > 0 ? "#fff8f5" : "#fff",
                border: quantity > 0 ? "1.5px solid #e16036" : "1.5px solid transparent",
                boxShadow: quantity > 0 ? "0 4px 48px rgba(225,96,54,0.07)" : "0 4px 48px rgba(0,0,0,0.04)",
              }}
            >
              <div className="flex items-start gap-3">
                {/* Ticket Image */}
                <div
                  className={`relative shrink-0 w-28 aspect-square rounded-lg overflow-hidden bg-gray-100 ${!isSoldOut ? "cursor-pointer" : "cursor-default"}`}
                  onClick={() => {
                    if (isSoldOut) return;
                    if (amazingpass && !isUserLogin) {
                      toast.warning("로그인이 필요해요.");
                      return;
                    }
                    navigate(`/ticket/${item.id}`, { state: { selectedDate: null, selectedSpotId: null } });
                  }}
                >
                  <img
                    src={item.thumbnail}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                  {isSoldOut && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                      <span className="text-white text-sm font-bold" style={{ fontFamily: "Gmarket-B" }}>품절</span>
                    </div>
                  )}
                </div>

                {/* Ticket Info */}
                <div className="flex-1 min-w-0 py-0.5 flex flex-col gap-3">
                  <div
                    className="flex items-start gap-1 cursor-pointer"
                    onClick={() => {
                      if (isSoldOut) return;
                      if (amazingpass && !isUserLogin) {
                        toast.warning("로그인이 필요해요.");
                        return;
                      }
                      navigate(`/ticket/${item.id}`, {
                        state: { selectedDate: null, selectedSpotId: null },
                      });
                    }}
                  >
                    <h3
                      className="text-lg font-medium leading-snug line-clamp-2 flex-1"
                      style={{ fontFamily: "Gmarket-M" }}
                    >
                      {item.name}
                    </h3>
                    {!isSoldOut && (
                      <div className="shrink-0 h-8 w-8 flex items-center justify-center">
                        {quantity > 0 ? (
                          <button
                            type="button"
                            aria-label={`${item.name} 장바구니에서 제거`}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#f4c8cf] bg-white text-[#d83f57] shadow-sm transition hover:border-[#f0a9b6] hover:bg-[#fff6f7]"
                            onClick={(event) => {
                              event.stopPropagation();
                              removeCartItem(item.id);
                            }}
                          >
                            <Trash2 size={16} strokeWidth={2.2} />
                          </button>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M5 3L9 7L5 11" stroke="#aaa" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    {user && user.membership !== "" && item.membership_price && item.membership_price != item.price && (
                      <span className="text-lg text-gray-400 line-through" style={{ fontFamily: "Gmarket-M" }}>
                        {item.price.toLocaleString()}원
                      </span>
                    )}
                    <p className="leading-none">
                      <span
                        className="text-lg  font-extrabold tracking-tight"
                        style={{
                          fontFamily: "Gmarket-B",
                          color: isSoldOut ? "#aaa"
                            : (user && user.membership !== "" && item.membership_price && item.membership_price != item.price)
                              ? "#22c55e"
                              : "#111",
                        }}
                      >
                        {(user && user.membership !== "" && item.membership_price && item.membership_price != item.price
                          ? item.membership_price
                          : item.price
                        ).toLocaleString()}
                      </span>
                      <span
                        className="text-lg font-extrabold ml-0.5"
                        style={{
                          fontFamily: "Gmarket-B",
                          color: isSoldOut ? "#aaa"
                            : (user && user.membership !== "" && item.membership_price && item.membership_price != item.price)
                              ? "#22c55e"
                              : "#111",
                        }}
                      >
                        원
                      </span>
                    </p>
                  </div>

                  {/* Quantity Selector */}
                  {!amazingpass && (
                    <div className="flex justify-end mt-1">
                      <QuantityButtonInput
                        size={28}
                        quantity={quantity}
                        onChange={(value) => {
                          if (value > item.remaining_stock) {
                            toast.error("구매 가능 개수를 초과하였습니다.");
                            return;
                          }
                          setCartItemQuantity(item, value);
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Fixed Bottom Bar */}
      {amazingpass ? (
        <div
          className="fixed bottom-16.5 left-1/2 -translate-x-1/2 w-full max-w-160 px-4 pb-4 z-50"
          ref={scrollRef}
        >
          <button
            className="w-full py-4 rounded-xl font-bold text-white text-base"
            style={{
              backgroundColor: "#e16036",
              fontFamily: "Gmarket-M"
            }}
          >
            구매하기
          </button>
        </div>
      ) : (
        <div
          className="fixed bottom-16.5 left-1/2 -translate-x-1/2 w-full max-w-160 bg-white/80 backdrop-blur-md border-t border-gray-200 z-50"
        >
          <div className="flex items-center justify-between px-7 py-3">
            {/* Total Price */}
            <div className={`flex flex-col transition-opacity duration-200 ${ticket_purchase_count === 0 ? "opacity-40" : "opacity-100"}`}>
              <span
                className="text-sm text-gray-600"
                style={{ fontFamily: "Gmarket-M" }}
              >
                합계금액
              </span>
              <span
                className="text-lg font-bold"
                style={{ fontFamily: "Gmarket-B" }}
              >
                {totalPrice.toLocaleString()}원
              </span>
            </div>

            {/* Purchase Button */}
            <Button
              disabled={!ticket_purchase_count}
              variant="primary"
              className="px-6"
              // style={{    boxShadow: "0 4px 12px rgba(225, 96, 54, 0.45), 0 1px 3px rgba(225, 96, 54, 0.3)", fontFamily: "Gmarket-M" }}
              onClick={() => {
                if (!isUserLogin) {
                  toast.warning("로그인이 필요해요.");
                  return;
                }
                navigate(`/order-ticket-multi`, {
                  state: { type: 1 },
                });
              }}
            >
              티켓 결제하기
            </Button>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};
export default CardSection;
